import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Section, Container, Badge } from '../components/UIComponents';
import { Button } from '../components/Button';
import { Phone, MapPin, Mail, Clock } from 'lucide-react';

const HeroSection = () => {
  return (
    <section className="bg-gradient-to-br from-slate-50 via-teal-50 to-blue-50 pt-8 md:pt-16 pb-16 md:pb-28">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <Badge variant="primary">Get In Touch</Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight mt-6">
            We're Here to Help
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
            Have questions about our surgical equipment or services? Reach out to our team of healthcare professionals. We're available 24/7.
          </p>
        </motion.div>
      </Container>
    </section>
  );
};

const ContactInfoSection = () => {
  return (
    <Section className="bg-white" title="Contact Information" subtitle="Get in touch with HCS Enterprises">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Phone */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-600 text-white rounded-full mb-4">
              <Phone size={32} />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Phone</h3>
            <p className="text-slate-600 mb-4">Call us for immediate assistance</p>
            <p className="text-xl font-bold text-teal-600">
              +917680944286
            </p>
            <p className="text-sm text-slate-500 mt-2">Available 24/7</p>
          </motion.div>

          {/* Address */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-600 text-white rounded-full mb-4">
              <MapPin size={32} />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Address</h3>
            <p className="text-slate-600 mb-4">Visit our facility</p>
            <p className="text-lg font-bold text-slate-900">
              12-2-460/1/1,2 Mahek's Vijaya Vihar,<br />Gudi Malkapur Road,<br />Hyderabad, Telangana 500006
            </p>
            <p className="text-sm text-slate-500 mt-2">All India Delivery</p>
          </motion.div>

          {/* Email */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-600 text-white rounded-full mb-4">
              <Mail size={32} />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Email</h3>
            <p className="text-slate-600 mb-4">Get in touch via email</p>
            <p className="text-lg font-bold text-teal-600">
              info@hcsent.com
            </p>
            <p className="text-sm text-slate-500 mt-2">Response in 24 hours</p>
          </motion.div>
        </div>
      </Container>
    </Section>
  );
};

const BusinessHoursSection = () => {
  return (
    <Section className="bg-slate-50">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto bg-white rounded-lg p-8 shadow-md"
        >
          <div className="flex items-center gap-4 mb-6">
            <Clock className="text-teal-600" size={32} />
            <h2 className="text-3xl font-bold text-slate-900">Business Hours</h2>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center pb-4 border-b border-slate-200">
              <span className="font-semibold text-slate-700">Monday - Saturday</span>
              <span className="text-slate-600">9:00 AM - 11:00 PM</span>
            </div>
            <div className="flex justify-between items-center pb-4">
              <span className="font-semibold text-slate-700">Sunday</span>
              <span className="text-slate-600">12:00 PM - 11:00 PM</span>
            </div>
          </div>

          <div className="mt-6 p-4 bg-teal-50 rounded-lg border border-teal-200">
            <p className="text-slate-700">
              <span className="font-semibold">📞 Emergency Support:</span> Available 24/7 for urgent medical equipment needs
            </p>
          </div>
        </motion.div>
      </Container>
    </Section>
  );
};

const LocationSection = () => {
  return (
    <Section className="bg-white" title="Our Facility">
      <Container>
        <div className="rounded-lg overflow-hidden shadow-lg h-96">
          <iframe
            title="HCS Enterprises Location Map"
            width="100%"
            height="100%"
            style={{ border: '0' }}
            loading="lazy"
            allowFullScreen={true}
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3802.243!2d78.3809!3d17.3812!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1sHealth%20care%20surgicals%20%26%20Medical%20HCS%2C%20Hyderabad!2s0x3bcb90cc00000001:0x1234567890abcdef!5e0!3m2!1sen!2sin!4v1712500800"
          ></iframe>
        </div>
        <div className="mt-8 text-center">
          <p className="text-slate-600 text-lg">
            📍 <strong>12-2-460/1/1,2 Mahek's Vijaya Vihar, Gudi Malkapur Road, Hyderabad, Telangana 500006</strong>
          </p>
          <p className="text-slate-500 mt-2">Click on the map to get directions</p>
        </div>
      </Container>
    </Section>
  );
};

const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // TODO: Send email or save to database
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <Section className="bg-slate-50" title="Send us a Message" subtitle="We'd love to hear from you">
      <Container>
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          onSubmit={handleSubmit}
          className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-md"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
                placeholder="Your Name"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
                placeholder="your@email.com"
              />
            </div>
          </div>

          {/* Phone */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
              placeholder="+91 (Your Phone)"
            />
          </div>

          {/* Subject */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Subject
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-teal-500 focus:outline-none transition-colors"
              placeholder="e.g., Product Inquiry"
            />
          </div>

          {/* Message */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="6"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:border-teal-500 focus:outline-none transition-colors resize-none"
              placeholder="Tell us more about your inquiry..."
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            size="lg"
            variant="primary"
            className="w-full"
          >
            Send Message
          </Button>

          <p className="text-sm text-slate-500 text-center mt-4">
            We'll respond to your message within 24 hours
          </p>
        </motion.form>
      </Container>
    </Section>
  );
};

const FAQSection = () => {
  return (
    <Section className="bg-white" title="Frequently Asked Questions" subtitle="Quick answers to common questions">
      <Container>
        <div className="max-w-3xl mx-auto space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-slate-50 p-6 rounded-lg"
          >
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              What is your delivery timeframe?
            </h3>
            <p className="text-slate-600">
              We typically deliver medical equipment within 24-48 hours across India. Express delivery options are available for urgent orders.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-slate-50 p-6 rounded-lg"
          >
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Are all products ISO certified?
            </h3>
            <p className="text-slate-600">
              Yes, all products in our catalog are ISO certified and undergo rigorous quality testing to ensure maximum safety and performance.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-slate-50 p-6 rounded-lg"
          >
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Do you offer bulk discounts?
            </h3>
            <p className="text-slate-600">
              Yes! We offer special pricing for bulk orders to hospitals, clinics, and healthcare facilities. Contact us for a custom quote.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="bg-slate-50 p-6 rounded-lg"
          >
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              What is your return policy?
            </h3>
            <p className="text-slate-600">
              We offer a 30-day money-back guarantee if you're not completely satisfied with your purchase. No questions asked!
            </p>
          </motion.div>
        </div>
      </Container>
    </Section>
  );
};

export default function Contact() {
  return (
    <div className="w-full bg-white">
      <HeroSection />
      <ContactInfoSection />
      <BusinessHoursSection />
      <ContactFormSection />
      <LocationSection />
      <FAQSection />
    </div>
  );
}
